import { isPlainObject } from 'es-toolkit';

/**
 * 임의의 JSON 직렬화 가능 값에 대한 "안정적 동등성 지문(fingerprint)" 유틸.
 *
 * 목적: 두 값이 내용상 같은지를, 깊은 비교 대신 짧은 문자열 비교 한 번으로 판정한다.
 * 비암호화(non-cryptographic) — 보안/위변조 방지가 아니라 동등성 비교 전용이다.
 */

/**
 * 순환 참조를 감지하며 노드를 방문한다. 현재 조상 경로(`ancestors`)에 이미 있는
 * 노드를 다시 만나면 순환이므로 throw 하고, 방문이 끝나면 경로에서 제거해 형제 가지의
 * 공유 참조(비순환 DAG)는 정상 통과시킨다.
 */
const withCycleGuard = <T>(
  node: object,
  ancestors: Set<object>,
  run: () => T,
): T => {
  if (ancestors.has(node)) {
    throw new TypeError('stableHash: circular reference detected');
  }

  ancestors.add(node);
  try {
    return run();
  } finally {
    ancestors.delete(node);
  }
};

/** 객체를 키 정렬한 stable 표현으로 재귀 변환한다(undefined 값 키는 제외). */
const toStableJsonValue = (value: unknown): unknown => {
  const visit = (current: unknown, ancestors: Set<object>): unknown => {
    if (Array.isArray(current)) {
      return withCycleGuard(current, ancestors, () =>
        current.map(item => visit(item, ancestors)),
      );
    }

    if (!isPlainObject(current)) return current;

    return withCycleGuard(current, ancestors, () =>
      Object.keys(current)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          const stableValue = visit(current[key], ancestors);
          if (stableValue !== undefined) acc[key] = stableValue;

          return acc;
        }, {}),
    );
  };

  return visit(value, new Set<object>());
};

/**
 * 키 순서와 무관하게 항상 같은 문자열을 만드는 결정적 JSON 직렬화.
 *
 * - 객체 키를 정렬해 `{a,b}` 와 `{b,a}` 가 같은 문자열이 되게 한다.
 * - `undefined` 값 키는 제외한다(`JSON.stringify` 와 동일한 취급).
 * - 배열 순서는 의미가 있으므로 보존한다.
 *
 * 동일 내용 → 동일 출력을 보장하므로 동등성 비교의 canonical form 으로 쓴다.
 */
export const stableStringify = (value: unknown): string =>
  JSON.stringify(toStableJsonValue(value));

/**
 * 문자열을 짧은(~11자) base36 토큰으로 압축하는 결정적 해시(cyrb53).
 *
 * 같은 입력 → 항상 같은 토큰. 서로 다른 입력은 (~2^-53 확률의 충돌을 제외하고)
 * 다른 토큰을 만든다. 내부 비트 연산 세부는 알 필요 없이 "문자열 → 짧고 안정적인
 * 지문"으로 이해하면 된다.
 *
 * @param value 해시할 문자열
 * @param seed  같은 입력에 서로 다른 지문이 필요할 때 쓰는 salt (기본 0)
 */
export const cyrb53 = (value: string, seed = 0): string => {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;

  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }

  h1 =
    Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
    Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 =
    Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
    Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
};

/**
 * 임의의 JSON 값을 안정적 동등성 지문 문자열로 변환한다.
 *
 * `stableStringify` 로 canonical 화한 뒤 `cyrb53` 로 압축한다. 두 값의 지문이 같으면
 * 내용이 같다고 본다(짧은 문자열 비교로 깊은 비교를 대체).
 */
export const stableHash = (value: unknown): string => {
  const json = stableStringify(value);
  return cyrb53(json, json.length);
};
